import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';

interface FormData {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  eventType: string;
  eventDate: string;
  budget: string;
  message: string;
}

interface FormErrors {
  [key: string]: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    eventType: '',
    eventDate: '',
    budget: '',
    message: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const eventTypes = [
    'Corporate Event',
    'Government Conference',
    'Product Launch',
    'Film Festival',
    'Trade Show',
    'Tourism Promotion',
    'Agricultural Expo',
    'Entertainment Event',
    'Other'
  ];

  const budgetRanges = [
    'Under ₹5 Lakhs',
    '₹5-10 Lakhs',
    '₹10-25 Lakhs',
    '₹25-50 Lakhs',
    '₹50 Lakhs - 1 Crore',
    'Above ₹1 Crore',
    'Prefer to discuss'
  ];

  const companyInfo = [
    {
      icon: Phone,
      label: 'Phone',
      value: '+91 982 213 7676',
      href: 'tel:+919822137676',
      description: 'Call us for immediate assistance'
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'mirchirepublic@gmail.com',
      href: 'mailto:mirchirepublic@gmail.com',
      description: 'Send us your project details'
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'Q-1, Fifth Floor, Silvio Heights, Opp. PWD Quarters, St. Inez, Panaji, Goa 403001',
      href: '#',
      description: 'Visit our creative studio'
    },
    {
      icon: Clock,
      label: 'Business Hours',
      value: 'Mon - Sat: 9:00 AM - 7:00 PM',
      href: '#',
      description: 'We\'re here when you need us'
    }
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Remove all non-digit characters except + at the beginning
      const cleanPhone = formData.phone.replace(/[\s\-().]/g, '');
      
      // Check if it's a valid phone number format
      const phoneRegex = /^(\+91|91|0)?[0-9]\d{9}$/; // Phone number format - can start with any digit
      const internationalRegex = /^\+[1-9]\d{1,14}$/; // International format
      
      if (!phoneRegex.test(cleanPhone) && !internationalRegex.test(cleanPhone)) {
        if (cleanPhone.length < 10) {
          newErrors.phone = 'Phone number must be at least 10 digits';
        } else if (cleanPhone.length > 15) {
          newErrors.phone = 'Phone number must not exceed 15 digits';
        } else if (!/^\+?[0-9\s\-().]+$/.test(formData.phone)) {
          newErrors.phone = 'Phone number can only contain digits, +, -, (), and spaces';
        } else {
          newErrors.phone = 'Please enter a valid phone number (e.g., 9876543210)';
        }
      }
    }

    if (!formData.eventType) {
      newErrors.eventType = 'Please select an event type';
    }

    if (formData.eventDate) {
      const selectedDate = new Date(formData.eventDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      
      if (selectedDate < today) {
        newErrors.eventDate = 'Event date cannot be in the past';
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Special handling for phone number - only allow digits and limit to 10
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, ''); // Remove all non-digits
      const limitedValue = digitsOnly.slice(0, 10); // Limit to 10 digits
      setFormData(prev => ({ ...prev, [name]: limitedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      
      // Web3Forms access key (you'll need to get this from https://web3forms.com)
      formDataToSubmit.append('access_key', 'ee0a136c-b688-4d97-add9-52004bdd57af3');
      
      
      
      // Add form data
      formDataToSubmit.append('company_name', formData.companyName);
      formDataToSubmit.append('contact_person', formData.contactPerson);
      formDataToSubmit.append('sender_email', formData.email);
      formDataToSubmit.append('phone', formData.phone);
      formDataToSubmit.append('address', formData.address);
      formDataToSubmit.append('event_type', formData.eventType);
      formDataToSubmit.append('event_date', formData.eventDate);
      formDataToSubmit.append('budget', formData.budget);
      formDataToSubmit.append('message', formData.message);
      
      // Add subject
      formDataToSubmit.append('subject', `New Contact Form Submission - ${formData.eventType || 'General Inquiry'}`);
      
      // Submit to Web3Forms
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSubmit
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIsSubmitted(true);
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          address: '',
          eventType: '',
          eventDate: '',
          budget: '',
          message: ''
        });
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Thank You | Mirchi Republic</title>
        </Helmet>
        <div className="pt-20 min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
          <Card className="text-center max-w-md mx-auto">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              We've received your inquiry and will get back to you within 24 hours.
            </p>
            <Button 
              variant="primary" 
              onClick={() => setIsSubmitted(false)}
            >
              Send Another Message
            </Button>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Us | Mirchi Republic</title>
        <meta name="description" content="Get in touch with Mirchi Republic Media Production. Contact us for event management, media production, and creative solutions." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-neutral-900 to-neutral-800 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 800 600" className="w-full h-full" fill="currentColor">
              <path d="M100 300 C 160 240, 240 280, 300 260 C 360 240, 440 280, 500 270 C 560 260, 640 300, 700 320 L 700 500 L 100 500 Z" />
            </svg>
          </div>
          
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Let's Create Magic Together
              </motion.h1>
              <motion.p
                className="text-xl lg:text-2xl opacity-90 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Ready to bring your vision to life? Get in touch and let's discuss how we can make your next event extraordinary.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card>
                  <h2 className="text-3xl font-bold mb-8">Get a Free Quote</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Web3Forms hidden fields */}
                    <input type="hidden" name="access_key" value="YOUR_ACCESS_KEY_HERE" />
                    <input type="hidden" name="subject" value={`New Contact Form Submission - ${formData.eventType || 'General Inquiry'}`} />
                    <input type="hidden" name="from_name" value="Mirchi Republic Website" />
                    <input type="hidden" name="template" value="table" />
                    <input type="hidden" name="content_type" value="html" />
                    <input type="hidden" name="reply_to" value={formData.email} />
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="companyName" className="block text-sm font-medium mb-2">
                          Company Name *
                        </label>
                        <input
                          type="text"
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors ${
                            errors.companyName 
                              ? 'border-error focus:ring-error' 
                              : 'border-neutral-300 dark:border-neutral-600'
                          } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                          placeholder="Your company name"
                        />
                        {errors.companyName && (
                          <p className="text-error text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.companyName}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="contactPerson" className="block text-sm font-medium mb-2">
                          Contact Person *
                        </label>
                        <input
                          type="text"
                          id="contactPerson"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors ${
                            errors.contactPerson 
                              ? 'border-error focus:ring-error' 
                              : 'border-neutral-300 dark:border-neutral-600'
                          } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                          placeholder="Your full name"
                        />
                        {errors.contactPerson && (
                          <p className="text-error text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.contactPerson}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors ${
                            errors.email 
                              ? 'border-error focus:ring-error' 
                              : 'border-neutral-300 dark:border-neutral-600'
                          } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                          placeholder="your@email.com"
                        />
                        {errors.email && (
                          <p className="text-error text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.email}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          maxLength={10}
                          pattern="[0-9]{10}"
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors ${
                            errors.phone 
                              ? 'border-error focus:ring-error' 
                              : 'border-neutral-300 dark:border-neutral-600'
                          } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                          placeholder="9876543210"
                        />
                        {errors.phone && (
                          <p className="text-error text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium mb-2">
                        Address
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        placeholder="Your business address"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="eventType" className="block text-sm font-medium mb-2">
                          Event Type *
                        </label>
                        <select
                          id="eventType"
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors ${
                            errors.eventType 
                              ? 'border-error focus:ring-error' 
                              : 'border-neutral-300 dark:border-neutral-600'
                          } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                        >
                          <option value="">Select event type</option>
                          {eventTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {errors.eventType && (
                          <p className="text-error text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.eventType}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="eventDate" className="block text-sm font-medium mb-2">
                          Preferred Event Date
                        </label>
                        <input
                          type="date"
                          id="eventDate"
                          name="eventDate"
                          value={formData.eventDate}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors ${
                            errors.eventDate 
                              ? 'border-error focus:ring-error' 
                              : 'border-neutral-300 dark:border-neutral-600'
                          } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                        />
                        {errors.eventDate && (
                          <p className="text-error text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.eventDate}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="budget" className="block text-sm font-medium mb-2">
                        Budget Range
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                      >
                        <option value="">Select budget range</option>
                        {budgetRanges.map(range => (
                          <option key={range} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">
                        Project Details *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent transition-colors resize-none ${
                          errors.message 
                            ? 'border-error focus:ring-error' 
                            : 'border-neutral-300 dark:border-neutral-600'
                        } bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100`}
                        placeholder="Tell us about your event requirements, goals, and any specific needs..."
                      />
                      {errors.message && (
                        <p className="text-error text-sm mt-1 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={isSubmitting}
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Send Inquiry
                    </Button>
                  </form>
                </Card>
              </motion.div>

              {/* Company Info */}
              <motion.div
                className="space-y-8"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div>
                  <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
                  <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    We're here to help bring your vision to life. Whether you're planning a corporate event, government conference, or creative campaign, our team is ready to deliver exceptional results.
                  </p>
                </div>

                <div className="space-y-6">
                  {companyInfo.map((item, index) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                    >
                      <Card className="hover:shadow-lg transition-shadow duration-300">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-brand-1/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <item.icon className="w-6 h-6 text-brand-1" />
                          </div>
                          <div className="flex-grow">
                            <h4 className="font-semibold text-lg mb-1">{item.label}</h4>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
                              {item.description}
                            </p>
                            {item.href.startsWith('#') ? (
                              <p className="font-medium">{item.value}</p>
                            ) : (
                              <a
                                href={item.href}
                                className="font-medium text-brand-1 hover:text-brand-2 transition-colors duration-200"
                              >
                                {item.value}
                              </a>
                            )}
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                <Card>
                  <h3 className="text-xl font-semibold mb-4">Why Choose Mirchi Republic?</h3>
                  <ul className="space-y-3">
                    {[
                      'Free consultation and quote',
                      '24/7 dedicated project support',
                      'End-to-end event management',
                      'Government and corporate trusted',
                      'Award-winning creative team',
                      'On-time, on-budget delivery'
                    ].map((feature, index) => (
                      <motion.li
                        key={feature}
                        className="flex items-center space-x-3"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
                      >
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </motion.li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;